import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Reuse the proven, action-critical internals verbatim (start/stop/schedule/scale/idle
// handlers, payloads, dialogs and cross-page selection all live in these exported
// sub-components). Stage 1 only re-frames them in the new page shell.
import {
  CurrentInstanceComponent,
  ScheduledEvents,
  IdleEventsComponent,
  ActivityLogComponent,
} from '../components/AzureInventory';

import { INVENTORY_EVENTS, CONST_SITE_LABEL, useCurrentInstanceType } from '../components/common/Helper';

const TYPE_LABELS = {
  vm: 'Virtual Machine',
  vmss: 'VM Scale Set',
  mysql: 'MySQL Database',
  postgresql: 'PostgreSQL Database',
  sql: 'SQL Database',
};

const AzureInventory = () => {
  const location = useLocation();
  const { type } = useCurrentInstanceType();

  const [activeTab, setActiveTab] = useState(INVENTORY_EVENTS.INSTANCES);

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | ${(type || '').toLocaleUpperCase()} Inventory`;
  }, [location.pathname, type]);

  // Idle tab only exists for VM — reset to Instances whenever the resource type changes
  // so we never land on a tab that isn't rendered for the new type.
  useEffect(() => {
    setActiveTab(INVENTORY_EVENTS.INSTANCES);
  }, [type]);

  const tabs = [
    { key: INVENTORY_EVENTS.INSTANCES, label: INVENTORY_EVENTS.INSTANCES },
    { key: INVENTORY_EVENTS.SCHEDULED, label: INVENTORY_EVENTS.SCHEDULED },
    ...(type === 'vm' ? [{ key: INVENTORY_EVENTS.IDLE, label: INVENTORY_EVENTS.IDLE }] : []),
    { key: INVENTORY_EVENTS.ACTIVITY, label: INVENTORY_EVENTS.ACTIVITY },
  ];

  const title = TYPE_LABELS[type] ?? 'Inventory';

  return (
    <>
      <div className="breadcrumb">
        Inventory <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>{title}</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>{title} Inventory</h1>
          <div className="meta">Manage instances · start, stop, schedule shutdown, scale, and idle policies</div>
        </div>
      </div>

      <div className="sub-tabs">
        {tabs.map((t) => (
          <div
            key={t.key}
            className={`sub-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </div>
        ))}
      </div>

      <div className="inventory-tab-body" style={{ marginTop: '12px' }}>
        {activeTab === INVENTORY_EVENTS.INSTANCES && <CurrentInstanceComponent />}
        {activeTab === INVENTORY_EVENTS.SCHEDULED && <ScheduledEvents />}
        {activeTab === INVENTORY_EVENTS.IDLE && type === 'vm' && <IdleEventsComponent />}
        {activeTab === INVENTORY_EVENTS.ACTIVITY && <ActivityLogComponent />}
      </div>
    </>
  );
};

export default AzureInventory;
